import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Alert,
  ListGroup,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import OperationProgress from '../components/OperationProgress';
import { useCopyProgress } from '../hooks/useCopyProgress';
import EditListField from '../components/EditListField';
import {
  validateFleetServiceHistoryForm,
  validateField,
  ValidationRules,
  hasErrors,
  formatErrors,
} from '../utils/validation';

const SERVICE_TYPE_DEFAULTS = ['Service', 'Repair'];

function emptyForm() {
  return {
    stock_no: '',
    type: '',
    service_date: '',
    notes: '',
    cost_parts: '',
    cost_labour: '',
    meter_reading: '',
  };
}

function toForm(record) {
  if (!record) {
    return emptyForm();
  }
  return {
    stock_no: record.stock_no || '',
    type: record.type || '',
    service_date: record.service_date ? String(record.service_date).split('T')[0] : '',
    notes: record.notes || '',
    cost_parts: record.cost_parts ?? '',
    cost_labour: record.cost_labour ?? '',
    meter_reading: record.meter_reading ?? '',
  };
}

const SYSTEM_FIELDS = new Set(['id', 'created_at', 'updated_at', 'tenant_id', 'user_id']);

const GRID_FIELD_LABELS = {
  type: 'Type',
  service_date: 'Service date',
  meter_reading: 'Meter reading',
  cost_parts: 'Cost parts',
  cost_labour: 'Cost labour',
  notes: 'Notes',
};

function buildRecordPayload(record, overrides = {}) {
  const payload = {};
  for (const [key, value] of Object.entries(record)) {
    if (!SYSTEM_FIELDS.has(key)) {
      payload[key] = value;
    }
  }
  return { ...payload, ...overrides };
}

function buildCopyPayload(record) {
  return buildRecordPayload(record);
}

function formatHireStockLabel(item) {
  if (!item) {
    return '';
  }
  return `${item.name} (${item.stock_no})`;
}

function formatGridEditValue(row, field) {
  if (field === 'service_date') {
    return row.service_date ? String(row.service_date).split('T')[0] : '';
  }
  if (field === 'meter_reading' || field === 'cost_parts' || field === 'cost_labour') {
    return row[field] == null || row[field] === '' ? '' : String(row[field]);
  }
  return row[field] == null ? '' : String(row[field]);
}

function formatGridDisplayValue(row, field, formatDate) {
  if (field === 'service_date') {
    return formatDate(row.service_date);
  }
  if (field === 'meter_reading' || field === 'cost_parts' || field === 'cost_labour') {
    return row[field] ?? '—';
  }
  return row[field] || '—';
}

function parseGridSaveValue(field, raw) {
  if (field === 'service_date') {
    return raw || null;
  }
  if (field === 'meter_reading' || field === 'cost_parts' || field === 'cost_labour') {
    return raw === '' ? null : raw;
  }
  return raw.trim();
}

function gridValuesEqual(field, current, next) {
  if (field === 'meter_reading') {
    const currentNum = current == null || current === '' ? null : Number(current);
    const nextNum = next == null || next === '' ? null : Number(next);
    if (currentNum === null && nextNum === null) {
      return true;
    }
    if (currentNum === null || nextNum === null || Number.isNaN(nextNum)) {
      return String(current ?? '') === String(next ?? '');
    }
    return currentNum === nextNum;
  }
  if (field === 'cost_parts' || field === 'cost_labour') {
    const currentNum = current == null || current === '' ? null : Number(current);
    const nextNum = next == null || next === '' ? null : Number(next);
    if (currentNum === null && nextNum === null) {
      return true;
    }
    if (currentNum === null || nextNum === null || Number.isNaN(nextNum)) {
      return String(current ?? '') === String(next ?? '');
    }
    return currentNum === nextNum;
  }
  if (field === 'service_date') {
    const currentDate = current ? String(current).split('T')[0] : '';
    const nextDate = next ? String(next).split('T')[0] : '';
    return currentDate === nextDate;
  }
  const currentText = current == null ? '' : String(current).trim();
  const nextText = next == null ? '' : String(next).trim();
  return currentText === nextText;
}

function EditableGridCell({
  row,
  field,
  type = 'text',
  multiline = false,
  editingCell,
  editingValue,
  savingCell,
  onStartEdit,
  onChange,
  onSave,
  onKeyDown,
  maxLength,
  formatDate,
}) {
  const cellKey = `${row.id}-${field}`;
  const isEditing = editingCell === cellKey;
  const isSaving = savingCell === cellKey;
  const displayValue = formatGridDisplayValue(row, field, formatDate);

  if (isEditing) {
    return (
      <Form.Control
        as={multiline ? 'textarea' : undefined}
        type={multiline ? undefined : type}
        rows={multiline ? 2 : undefined}
        size="sm"
        value={editingValue}
        maxLength={maxLength}
        step={type === 'number' ? '0.01' : undefined}
        min={type === 'number' ? '0' : undefined}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => onSave(row, field)}
        onKeyDown={(e) => onKeyDown(e, row, field, multiline)}
        autoFocus
        disabled={isSaving}
        style={{
          minWidth: multiline ? '180px' : type === 'date' ? '140px' : '100px',
          maxWidth: multiline ? '220px' : undefined,
        }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      style={{
        cursor: 'pointer',
        display: 'inline-block',
        minWidth: '48px',
        maxWidth: multiline ? '220px' : undefined,
        wordWrap: 'break-word',
      }}
      onClick={() => onStartEdit(row, field)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(row, field);
        }
      }}
      title="Click to edit"
    >
      {displayValue}
    </span>
  );
}

function FleetServiceHistoryModal({ record, onClose, onSave }) {
  const [formData, setFormData] = useState(emptyForm());
  const [hireStockItems, setHireStockItems] = useState([]);
  const [stockSearch, setStockSearch] = useState('');
  const [showStockMatches, setShowStockMatches] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchHireStockItems = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/hire-stock/stock-numbers', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHireStockItems(response.data);
      } catch (error) {
        console.error('Failed to fetch hire stock items:', error);
      }
    };
    fetchHireStockItems();
  }, []);

  useEffect(() => {
    setFormData(toForm(record));
    setSaveError('');
    setValidationErrors({});
    setShowStockMatches(false);
  }, [record]);

  useEffect(() => {
    const code = formData.stock_no;
    if (!code) {
      setStockSearch('');
      return;
    }
    const match = hireStockItems.find((item) => item.stock_no === code);
    setStockSearch(match ? formatHireStockLabel(match) : code);
  }, [formData.stock_no, hireStockItems, record]);

  const filteredHireStockItems = useMemo(() => {
    const query = stockSearch.trim().toLowerCase();
    const items = query
      ? hireStockItems.filter((item) => {
          const code = (item.stock_no || '').toLowerCase();
          const name = (item.name || '').toLowerCase();
          return code.includes(query) || name.includes(query);
        })
      : hireStockItems;

    return items.slice(0, 20);
  }, [hireStockItems, stockSearch]);

  const handleSelectHireStockItem = (item) => {
    setFormData((prev) => ({ ...prev, stock_no: item.stock_no }));
    setStockSearch(formatHireStockLabel(item));
    setShowStockMatches(false);
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.stock_no;
      return next;
    });
    setSaveError('');
  };

  const handleStockSearchChange = (value) => {
    setStockSearch(value);
    setShowStockMatches(true);

    const trimmed = value.trim().toLowerCase();
    const exactMatch = hireStockItems.find(
      (item) =>
        item.stock_no?.toLowerCase() === trimmed ||
        formatHireStockLabel(item).toLowerCase() === trimmed
    );

    setFormData((prev) => ({
      ...prev,
      stock_no: exactMatch ? exactMatch.stock_no : '',
    }));

    if (validationErrors.stock_no) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.stock_no;
        return next;
      });
    }
    setSaveError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setSaveError('');
  };

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setSaveError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateFleetServiceHistoryForm(formData);
    if (hasErrors(errors)) {
      setValidationErrors(errors);
      setSaveError(formatErrors(errors));
      return;
    }
    setValidationErrors({});
    setSaveError('');
    try {
      await onSave(formData);
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to save fleet service history';
      setSaveError(message);
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {record ? 'Edit Fleet Service History' : 'Add Fleet Service History'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {saveError && (
          <Alert variant="danger" className="mb-3">
            {saveError}
          </Alert>
        )}
        <Form noValidate onSubmit={handleSubmit}>
          <Container fluid>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 position-relative">
                  <Form.Label>Stock No *</Form.Label>
                  <Form.Control
                    type="search"
                    name="stock_no_search"
                    placeholder="Search by hire stock name or number..."
                    value={stockSearch}
                    onChange={(e) => handleStockSearchChange(e.target.value)}
                    onFocus={() => setShowStockMatches(true)}
                    onBlur={() => {
                      window.setTimeout(() => setShowStockMatches(false), 150);
                    }}
                    autoComplete="off"
                    isInvalid={!!validationErrors.stock_no}
                  />
                  {formData.stock_no && (
                    <Form.Text className="text-muted">
                      Selected stock no: {formData.stock_no}
                    </Form.Text>
                  )}
                  {showStockMatches && filteredHireStockItems.length > 0 && (
                    <ListGroup
                      className="position-absolute w-100 shadow-sm"
                      style={{ zIndex: 2, maxHeight: '220px', overflowY: 'auto' }}
                    >
                      {filteredHireStockItems.map((item) => (
                        <ListGroup.Item
                          key={item.stock_no}
                          action
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSelectHireStockItem(item)}
                        >
                          <div className="fw-medium">{item.name}</div>
                          <div className="small text-muted">Stock no: {item.stock_no}</div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                  {showStockMatches &&
                    stockSearch.trim() &&
                    filteredHireStockItems.length === 0 && (
                      <div className="small text-muted mt-1">
                        No matching hire stock items found
                      </div>
                    )}
                  {validationErrors.stock_no && (
                    <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                      {validationErrors.stock_no}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <EditListField
                  inputName="type"
                  label="Type"
                  required
                  active
                  refreshKey={record?.id ?? 'new'}
                  valuesUrl="/api/fleet-service-history/distinct-values?field=type"
                  seedDefaults={SERVICE_TYPE_DEFAULTS}
                  value={formData.type}
                  onChange={(type) => handleFieldChange('type', type)}
                  maxLength={50}
                  placeholder="Enter or select service type..."
                />
                {validationErrors.type && (
                  <div className="text-danger small mb-3">{validationErrors.type}</div>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Service Date *</Form.Label>
                  <Form.Control
                    type="date"
                    name="service_date"
                    value={formData.service_date}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.service_date}
                  />
                  {validationErrors.service_date && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.service_date}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Meter Reading</Form.Label>
                  <Form.Control
                    type="number"
                    step="1"
                    min="0"
                    name="meter_reading"
                    value={formData.meter_reading}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.meter_reading}
                  />
                  {validationErrors.meter_reading && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.meter_reading}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Notes *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    maxLength={2000}
                    isInvalid={!!validationErrors.notes}
                  />
                  {validationErrors.notes && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.notes}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Cost Parts</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="cost_parts"
                    value={formData.cost_parts}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.cost_parts}
                  />
                  {validationErrors.cost_parts && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.cost_parts}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Cost Labour</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="cost_labour"
                    value={formData.cost_labour}
                    onChange={handleChange}
                    isInvalid={!!validationErrors.cost_labour}
                  />
                  {validationErrors.cost_labour && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.cost_labour}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Container>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {record ? 'Update' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function FleetServiceHistory() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const { copyProgress, wrapCopy } = useCopyProgress();
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchRecords = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/fleet-service-history', {
        params: { page, limit: pagination.limit, search },
      });
      setRecords(response.data.fleetServiceHistory || []);
      setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      setError(`Failed to fetch fleet service history: ${err.message}`);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecords(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchRecords(newPage, searchTerm);
  };

  const handleSave = async (payload) => {
    try {
      if (editingRecord) {
        await axios.put(`/api/fleet-service-history/${editingRecord.id}`, payload);
        setSuccess('Fleet service history updated successfully');
      } else {
        await axios.post('/api/fleet-service-history', payload);
        setSuccess('Fleet service history created successfully');
      }
      setShowModal(false);
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      throw err;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }
    try {
      await axios.delete(`/api/fleet-service-history/${id}`);
      setSuccess('Record deleted successfully');
      fetchRecords(pagination.page, searchTerm);
    } catch {
      setError('Failed to delete record');
    }
  };

  const startGridEdit = (row, field) => {
    setEditingCell(`${row.id}-${field}`);
    setEditingValue(formatGridEditValue(row, field));
  };

  const cancelGridEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveGridEdit = async (row, field) => {
    const cellKey = `${row.id}-${field}`;
    if (editingCell !== cellKey) {
      return;
    }

    const parsed = parseGridSaveValue(field, editingValue);
    if (gridValuesEqual(field, row[field], parsed)) {
      cancelGridEdit();
      return;
    }

    const fieldError = validateField(
      field,
      parsed,
      ValidationRules.fleetServiceHistory[field],
      row
    );
    if (fieldError) {
      setError(fieldError);
      cancelGridEdit();
      return;
    }

    setSavingCell(cellKey);
    try {
      const response = await axios.put(`/api/fleet-service-history/${row.id}`, { [field]: parsed });
      setRecords((prev) =>
        prev.map((record) => (record.id === row.id ? { ...record, ...response.data } : record))
      );
      setSuccess(`${GRID_FIELD_LABELS[field] || 'Field'} updated`);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update record');
    } finally {
      setSavingCell(null);
      cancelGridEdit();
    }
  };

  const handleGridKeyDown = (e, row, field, multiline = false) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      saveGridEdit(row, field);
    } else if (e.key === 'Escape') {
      cancelGridEdit();
    }
  };

  const handleCopy = async (record) => {
    setCopyingId(record.id);
    setError('');
    try {
      await wrapCopy('Copying fleet service history...', 1, async (tick) => {
        await axios.post('/api/fleet-service-history', buildCopyPayload(record));
        tick({ success: true });
      });
      setSuccess('Fleet service history copied successfully');
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to copy fleet service history');
    } finally {
      setCopyingId(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL fleet service history records?')) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all fleet service history records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete('/api/fleet-service-history');
      setSuccess(`All records deleted successfully (${response.data.deletedCount || 0} records)`);
      fetchRecords(1, '');
    } catch {
      setError('Failed to delete all records');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-wrench me-3 text-secondary" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0">Fleet Service History</h1>
              <p className="text-muted mb-0">Track service and repair history for fleet assets</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button
            variant="primary"
            onClick={() => {
              setEditingRecord(null);
              setShowModal(true);
            }}
            className="me-2"
          >
            Add Record
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {copyProgress && (
        <OperationProgress
          title={copyProgress.title}
          processed={copyProgress.processed}
          total={copyProgress.total}
          success={copyProgress.success}
          failed={copyProgress.failed}
          variant="info"
        />
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by stock no, type, or notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center p-3">Loading...</div>
          ) : records.length === 0 ? (
            <div className="text-center p-3">No records found.</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover className="mb-3">
                  <thead>
                    <tr>
                      <th>Stock No</th>
                      <th>Type</th>
                      <th>Service Date</th>
                      <th>Meter</th>
                      <th>Cost Parts</th>
                      <th>Cost Labour</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.stock_no}</strong>
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="type"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            maxLength={50}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="service_date"
                            type="date"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="meter_reading"
                            type="number"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="cost_parts"
                            type="number"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="cost_labour"
                            type="number"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="notes"
                            multiline
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            maxLength={2000}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setEditingRecord(row);
                                setShowModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopy(row)}
                              disabled={copyingId === row.id}
                            >
                              {copyingId === row.id ? 'Copying...' : 'Copy'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(row.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <FleetServiceHistoryModal
          record={editingRecord}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </Container>
  );
}

export default FleetServiceHistory;
